import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { catchError, tap, throwError } from 'rxjs';

import { AuthActions } from '@shared/store/auth.actions';
import { EntityState } from '@shared/store/entity.state';
import { StoreActions } from '@shared/store/store.actions';

import { User } from '../models/user.model';
import { UserApiService } from '../services/user-api.service';
import { UserActions } from './user.actions';

export interface UserStateModel {
  user: User | null;
  isLoaded: boolean;
  hasError: boolean;
}

export const USER_STATE_TOKEN =
    new StateToken<UserStateModel>('UserState');

const defaultState: UserStateModel = {
  user: null,
  isLoaded: false,
  hasError: false,
};

@State<UserStateModel>({
  name: USER_STATE_TOKEN,
  defaults: defaultState,
})
@Injectable()
export class UserState {

  private _userService = inject(UserApiService);

  @Selector()
  static getUser(state: UserStateModel): User | null {
    return state.user;
  }

  @Selector()
  static isLoaded(state: UserStateModel): boolean {
    return state.isLoaded;
  }

  @Action(UserActions.Register)
  register(
      ctx: StateContext<UserStateModel>,
      action: UserActions.Register,
  ) {
    return this._userService
               .register$(action.payload)
               .pipe(
                   tap((user) => {
                     if (!user) {
                       return;
                     }
                     ctx.patchState({
                       user,
                       ...EntityState.getApiLoadSuccessConfig(),
                     });
                   }),
                   catchError((err) => {
                     ctx.patchState(EntityState.getApiLoadErrorConfig());
                     return throwError(() => err);
                   }),
               );
  }

  @Action(UserActions.GetMe)
  getMe(ctx: StateContext<UserStateModel>) {
    return this._userService
               .getMe$()
               .pipe(
                   tap((user) => {
                     if (!user) {
                       return;
                     }
                     ctx.patchState({
                       user,
                       ...EntityState.getApiLoadSuccessConfig(),
                     });
                   }),
               );
  }

  @Action(UserActions.Update)
  update(
      ctx: StateContext<UserStateModel>,
      action: UserActions.Update,
  ) {
    return this._userService
               .update$(action.payload)
               .pipe(
                   tap((user) => {
                     if (!user) {
                       return;
                     }
                     ctx.patchState({
                       user,
                       ...EntityState.getApiLoadSuccessConfig(),
                     });
                   }),
               );
  }

  @Action(UserActions.Delete)
  delete(ctx: StateContext<UserStateModel>) {
    return this._userService
               .delete$()
               .pipe(
                   tap(() => {
                     ctx.patchState({
                       user: null,
                       ...EntityState.getApiLoadSuccessConfig(),
                     });
                   }),
               );
  }

  @Action([
    UserActions.RegisterFailed,
    UserActions.GetMeFailed,
    UserActions.UpdateFailed,
    UserActions.DeleteFailed,
  ])
  requestFailed(ctx: StateContext<UserStateModel>) {
    ctx.patchState(EntityState.getApiLoadErrorConfig());
  }

  @Action([
    StoreActions.ResetLoadedState,
    UserActions.ResetLoadedState,
  ])
  resetLoadedState(ctx: StateContext<UserStateModel>) {
    ctx.patchState({
      isLoaded: defaultState.isLoaded,
      hasError: defaultState.hasError,
    });
  }

  @Action([
    UserActions.ResetState,
    StoreActions.ResetStore,
    AuthActions.Unauthorized,
  ])
  resetState(ctx: StateContext<UserStateModel>) {
    ctx.setState({
      ...defaultState,
    });
  }
}
